import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../app/app.environment';

export interface ClientTemplate {
  id: number;
  client_id: number;
  template_id: number;
  image: string;
  format: string; // 'A4', 'A5', '3inch', etc.
  default: number; // 1 = default, 0 = not default
  clientselected: number;
  status: number;
  templatejson?: string; // JSON configuration for dynamic templates (template_id >= 8)
  createdAt: string;
  updatedAt: string;
}

export interface ClientTemplateResponse {
  status: number;
  message: string;
  data: ClientTemplate[];
}

@Injectable({
  providedIn: 'root',
})
export class ClientTemplateService {
  private apiUrl = environment.apiURL + 'v1/order';
  private templatesSubject = new BehaviorSubject<ClientTemplate[]>([]);
  public templates$ = this.templatesSubject.asObservable();

  constructor(private http: HttpClient) { }

  /**
   * Fetch client templates from API
   * @param clientId - The client ID
   */
  getClientTemplates(clientId: number): Observable<ClientTemplate[]> {
    console.log('Fetching client templates for client_id:', clientId);
    return this.http
      .get<ClientTemplateResponse>(
        `${this.apiUrl}/getclienttemplate/${clientId}`,
      )
      .pipe(
        map((response) => response.data),
        tap((templates) => this.templatesSubject.next(templates)),
      );
  }

  /**
   * Get the default template
   */
  getDefaultTemplate(): ClientTemplate | null {
    const templates = this.templatesSubject.value;

    return templates.find((t) => t.default === 1 && t.status === 1) || null;
  }

  /**
   * Get template by ID
   */
  getTemplateById(templateId: number): ClientTemplate | null {
    const templates = this.templatesSubject.value;
    return (
      templates.find((t) => t.template_id === templateId && t.status === 1) ||
      null
    );
  }

  /**
   * Get template by format
   */
  getTemplateByFormat(format: string): ClientTemplate | null {
    const templates = this.templatesSubject.value;
    return (
      templates.find(
        (t) =>
          t.format.toLowerCase() === format.toLowerCase() && t.status === 1,
      ) || null
    );
  }

  /**
   * Get all active templates
   */
  getActiveTemplates(): ClientTemplate[] {
    return this.templatesSubject.value.filter((t) => t.status === 1);
  }

  /**
   * Map template_id to template name
   * Based on your mapping:
   * 1 - thermal template (default)
   * 2 - A4 -> ptr-enterprise
   * 3 - A5 -> rathna-shree
   * 4-7 - Static templates
   * 8 - hsncode-thermal (HSN Code Thermal 80mm)
   * 9+ - Dynamic JSON-driven templates
   */
  getTemplateNameById(templateId: number): string {
    console.log(templateId)
    // Handle dynamic templates (template_id >= 9)
    if (templateId == 8) {
      return 'dynamic-template'; // All dynamic templates use the same handler
    }


    // Handle static templates (template_id 1-7)
    switch (templateId) {
      case 1:
        return 'default'; // thermal template
      case 2:
        return 'ptr-enterprise'; // A4
      case 3:
        return 'rathna-shree'; // A5
      case 4:
        return 'new-jaffer'; // A3
      case 5:
        return 'a4-version4'; // A4 Version 4
      case 6:
        return 'a5-version1'; // A5 Version 1
      case 7:
        return 'thermal-version'; // Thermal Version
      case 9:
        return 'hsncode-thermal'; // HSN Code Thermal (80mm)
      case 10:
        return 'medical-a4-template';
      case 11:
        return 'letter-head-a4';
      default:
        return 'default'; // fallback to thermal
    }
  }

  /**
   * Get the template name for PDF generation
   * Returns the default template name or fallback to 'default'
   */
  getDefaultTemplateName(): string {
    const defaultTemplate = this.getDefaultTemplate();
    if (defaultTemplate) {
      return this.getTemplateNameById(defaultTemplate.template_id);
    }
    return 'default'; // fallback
  }

  /**
   * Get template name by format
   */
  getTemplateNameByFormat(format: string): string {
    const template = this.getTemplateByFormat(format);
    if (template) {
      return this.getTemplateNameById(template.template_id);
    }
    return 'default'; // fallback
  }

  /**
   * Clear cached templates
   */
  clearTemplates(): void {
    this.templatesSubject.next([]);
  }
}
